import React, { useState, useRef } from 'react';
import Button from '../Button/Button';
import './UploadDocumentModal.css';

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, type: string, commentaire?: string) => Promise<void>;
  preselectedType?: string;
}

const DOCUMENT_TYPE_BACKEND_MAP: Record<string, string> = {
  "Déclaration sur l'honneur": 'declaration_honneur',
  'Extrait K-Bis': 'kbis',
  "Attestations d'assurance": 'rc_pro',
  'Garantie décennale': 'garantie_decennale',
  'Attestation fiscale et sociale': 'attestation_fiscale',
  'Agréments, certifications, habilitations': 'agrements',
  "CV de l'équipe encadrante": 'cv_equipe',
  'RIB': 'rib',
};

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  preselectedType
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedType] = useState<string>(preselectedType || '');
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setError(null);
      setIsDragOver(false);
    }
  }, [isOpen]);

  // 20 Mo pour le CV de l'equipe encadrante, 10 Mo pour les autres documents
  const isCvEquipe = DOCUMENT_TYPE_BACKEND_MAP[selectedType] === 'cv_equipe';
  const maxSizeMo = isCvEquipe ? 20 : 10;

  const validateFile = (file: File): string | null => {
    if (file.type !== 'application/pdf') return 'Seuls les fichiers PDF sont acceptés';
    if (file.size > maxSizeMo * 1024 * 1024) return `Le fichier est trop volumineux (max ${maxSizeMo} Mo)`;
    return null;
  };


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { setError(err); setSelectedFile(null); return; }
    setError(null);
    setSelectedFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = Array.from(e.dataTransfer.files).find(f => f.type === 'application/pdf');
    if (!file) { setError('Seuls les fichiers PDF sont acceptés'); return; }
    const err = validateFile(file);
    if (err) { setError(err); return; }
    setError(null);
    setSelectedFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) { setError('Veuillez sélectionner un fichier'); return; }
    const type = selectedType || preselectedType || '';
    if (!type) { setError('Type de document manquant'); return; }
    try {
      setUploading(true);
      setError(null);
      const backendType = DOCUMENT_TYPE_BACKEND_MAP[type] || type;
      await onUpload(selectedFile, backendType);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="upload-document-modal-overlay" onClick={onClose}>
      <div className="upload-document-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="upload-document-modal-header">
          <h2>{preselectedType || 'Ajouter un document'}</h2>
          <button className="upload-document-modal-close" onClick={onClose} type="button">×</button>
        </div>

        <form onSubmit={handleSubmit} className="upload-document-form">
          {/* Zone drag-and-drop */}
          <div
            className={`upload-dropzone${isDragOver ? ' upload-dropzone--over' : ''}${selectedFile ? ' upload-dropzone--selected' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !selectedFile && fileInputRef.current?.click()}
            style={{ cursor: selectedFile ? 'default' : 'pointer' }}
          >
            {selectedFile ? (
              <>
                <div className="upload-dropzone-icon upload-dropzone-icon--ok">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="upload-dropzone-filename">{selectedFile.name}</p>
                <p className="upload-dropzone-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} Mo</p>
                <button type="button" className="upload-dropzone-change" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setError(null); }}>
                  Changer de fichier
                </button>
              </>
            ) : (
              <>
                <div className="upload-dropzone-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12l-3-3m0 0l-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                </div>
                <p className="upload-dropzone-label">Glissez votre PDF ici</p>
                <p className="upload-dropzone-sublabel">ou</p>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <Button
                    type="button"
                    value="Parcourir"
                    variant="primary"
                    size="small"
                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                    disabled={uploading}
                    style={{ width: '25%' }}
                  />
                </div>
                <p className="upload-dropzone-hint">PDF uniquement • max {maxSizeMo} Mo</p>
              </>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            disabled={uploading}
            style={{ display: 'none' }}
          />

          {error && <div className="upload-error-message">{error}</div>}

          <div className="upload-document-modal-actions">
            <Button type="button" onClick={onClose} value="Annuler" variant="secondary" disabled={uploading} />
            <Button type="submit" value={uploading ? 'Envoi en cours...' : 'Envoyer'} disabled={uploading || !selectedFile} />
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadDocumentModal;
